import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import {
  Dialog,
  DialogContent,
  IconButton,
  Typography,
  Box,
  Button,
  CircularProgress,
  TextField,
  Alert,
  InputAdornment,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CheckIcon from "@mui/icons-material/Check";
import CreditCardIcon from "@mui/icons-material/CreditCard";
import WarningIcon from "@mui/icons-material/Warning";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import EditIcon from "@mui/icons-material/Edit";
import {
  requestCopayCard,
  pollCopayCardStatus,
  submitManualCopayCard,
} from "../../api/aedificare";
import { updateProgramDetails } from "../../api/oppurtunity";

const FIELD_LABELS = {
  phoneNumber: "Phone Number",
  email: "Email Address",
  line1: "Address Line 1",
  line2: "Address Line 2",
  city: "City",
  state: "State",
  postalCode: "Postal Code",
  pcn: "PCN",
  bin: "BIN",
};

const valueContainerStyle = {
  backgroundColor: "#F7F8FB",
  borderRadius: "12px",
  padding: "8px 16px",
  display: "flex",
  flexDirection: "column",
  border: "1px solid #E8E8E9",
  width: "100%",
  maxWidth: "460px",
  margin: "16px auto 0 auto",
};

const HandIcon = ({ size = 20, ...props }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="#B78103" {...props}>
    <path d="M19 13c.02.15.02.29.02.43 0 3.31-2.69 6-6 6H9c-3.31 0-6-2.69-6-6v-3.5C3 9.12 3.88 8.25 5 8.25c.34 0 .66.09.94.24V4.75C5.94 3.78 6.78 3 7.75 3c.97 0 1.81.78 1.81 1.75V2.75C9.56 1.78 10.4 1 11.38 1c.97 0 1.81.78 1.81 1.75v1C13.19 2.78 14.03 2 15 2c.97 0 1.81.78 1.81 1.75V8.15c.61-.1 1.25.32 1.38 1.05l.81 3.8z" />
  </svg>
);

HandIcon.propTypes = {
  // NOSONAR
  size: PropTypes.number,
};

const formatToUsDate = (isoStr) => {
  if (!isoStr) return "";
  if (/^\d{2}\/\d{2}\/\d{4}$/.test(isoStr)) return isoStr;
  const match = isoStr.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (match) {
    const [, y, m, d] = match;
    return `${m}/${d}/${y}`;
  }
  return isoStr;
};

const isValidDateString = (dateStr) => {
  if (!dateStr?.trim()) return true;
  const trimmed = dateStr.trim();

  // Check format MM/DD/YYYY
  const formatRegex = /^(0[1-9]|1[0-2])\/(0[1-9]|[12]\d|3[01])\/\d{4}$/;
  if (!formatRegex.test(trimmed)) return false;

  const parts = trimmed.split("/");
  const month = Number.parseInt(parts[0], 10);
  const day = Number.parseInt(parts[1], 10);
  const year = Number.parseInt(parts[2], 10);

  if (month < 1 || month > 12) return false;
  if (year < 1900 || year > 2100) return false;

  const daysInMonths = [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
  if (
    month === 2 &&
    ((year % 4 === 0 && year % 100 !== 0) || year % 400 === 0)
  ) {
    daysInMonths[1] = 29;
  }

  if (day < 1 || day > daysInMonths[month - 1]) return false;

  // Complete date must be greater than today
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const enteredDate = new Date(year, month - 1, day);
  if (enteredDate <= today) return false;

  return true;
};

const formatDateInput = (value, previousValue = "") => {
  // Only allow digits and slashes
  let clean = value.replaceAll(/[^0-9/]/g, "");

  // Handle backspacing on a slash
  if (previousValue.length > value.length) {
    const diffIndex = previousValue
      .split("")
      .findIndex((char, index) => value[index] !== char);
    if (diffIndex !== -1 && previousValue[diffIndex] === "/") {
      const beforeSlash = previousValue.slice(0, diffIndex - 1);
      const afterSlash = previousValue.slice(diffIndex + 1);
      clean = beforeSlash + afterSlash;
    }
  }

  const digits = clean.replaceAll("/", "");

  let formatted = "";
  if (digits.length > 0) {
    formatted += digits.slice(0, 2);
  }
  if (digits.length > 2) {
    formatted += "/" + digits.slice(2, 4);
  }
  if (digits.length > 4) {
    formatted += "/" + digits.slice(4, 8);
  }

  // Preserve trailing slash if typed manually at correct boundary
  if (
    clean.endsWith("/") &&
    (formatted.length === 2 || formatted.length === 5)
  ) {
    formatted += "/";
  }

  return formatted;
};

const isPresent = (value) => value !== undefined && value !== null;

const getOpportunityId = (opportunity) =>
  opportunity?.programId ||
  opportunity?.memberProgramId ||
  opportunity?.opportunityId ||
  opportunity?.id;

const pickPerFillAmount = (initialCardData, opportunity) => {
  if (isPresent(initialCardData?.perFillAmount)) {
    return initialCardData.perFillAmount;
  }
  if (isPresent(opportunity?.perFillAmount)) {
    return opportunity.perFillAmount;
  }
  if (isPresent(opportunity?.program?.perFillAmount)) {
    return opportunity.program.perFillAmount;
  }
  return null;
};

const formatManualPerFillAmount = (initialCardData, opportunity) => {
  const val = pickPerFillAmount(initialCardData, opportunity);
  if (val === null || val === "") return "$150.00";
  if (typeof val === "number" || !Number.isNaN(Number(val))) {
    return `$${Number(val).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  }
  return String(val).startsWith("$") ? val : `$${val}`;
};

const formatManualAnnualValue = (initialCardData) => {
  if (initialCardData.annualValue) return initialCardData.annualValue;
  if (isPresent(initialCardData.annualBenefitAmount)) {
    return `$${Number(initialCardData.annualBenefitAmount).toLocaleString("en-US")}`;
  }
  return "";
};

const splitMemberName = (memberData) => {
  const firstName = memberData?.firstName || "";
  const lastName = memberData?.lastName || "";
  if (!firstName && memberData?.name) {
    const parts = memberData.name.trim().split(/\s+/);
    return {
      firstName: parts[0] || "",
      lastName: parts.slice(1).join(" ") || "",
    };
  }
  return { firstName, lastName };
};

const normalizeGender = (memberData) => {
  const gender = memberData?.gender || memberData?.sex || "Male";
  if (typeof gender !== "string") return gender;
  const gLower = gender.toLowerCase();
  if (gLower.startsWith("m")) return "Male";
  if (gLower.startsWith("f")) return "Female";
  return gender;
};

const normalizeDateOfBirth = (dob) => {
  if (!dob) return "";
  try {
    const d = new Date(dob);
    return Number.isNaN(d.getTime()) ? "" : d.toISOString().split("T")[0];
  } catch (e) {
    console.error("Failed to parse member DOB:", e);
    return dob;
  }
};

// Format Expiration Date (DD/MM/YYYY, DD-MM-YYYY, DDMMYYYY to YYYY-MM-DD)
const formatToIso = (dateStr) => {
  if (!dateStr) return "";

  // If it's already YYYY-MM-DD, return it
  if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) return dateStr;

  // Clean the input to keep only digits, slashes, and dashes
  const cleaned = dateStr.replaceAll(/[^0-9/-]/g, "");

  // Try splitting by slash
  if (cleaned.includes("/")) {
    const parts = cleaned.split("/");
    if (parts.length === 3) {
      const m = parts[0].padStart(2, "0");
      const d = parts[1].padStart(2, "0");
      const y = parts[2];
      return `${y}-${m}-${d}`;
    }
  }

  // Try splitting by dash
  if (cleaned.includes("-")) {
    const parts = cleaned.split("-");
    if (parts.length === 3) {
      const m = parts[0].padStart(2, "0");
      const d = parts[1].padStart(2, "0");
      const y = parts[2];
      return `${y}-${m}-${d}`;
    }
  }

  // If it's a continuous string of 8 digits (e.g. MMDDYYYY)
  const onlyDigits = cleaned.replaceAll(/\D/g, "");
  if (onlyDigits.length === 8) {
    const m = onlyDigits.substring(0, 2);
    const d = onlyDigits.substring(2, 4);
    const y = onlyDigits.substring(4, 8);
    return `${y}-${m}-${d}`;
  }

  return dateStr;
};

const isLeapYear = (year) =>
  (year % 4 === 0 && year % 100 !== 0) || year % 400 === 0;

const getDaysInMonth = (month, year) => {
  const daysInMonths = [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
  if (month === 2 && isLeapYear(year)) return 29;
  return daysInMonths[month - 1];
};

const validateDateComponents = (dateStr, fieldLabel) => {
  if (!dateStr?.trim()) {
    return null;
  }

  // Check format MM/DD/YYYY
  const formatRegex = /^(0[1-9]|1[0-2])\/(0[1-9]|[12]\d|3[01])\/\d{4}$/;
  if (!formatRegex.test(dateStr)) {
    return `Please enter a valid ${fieldLabel} in MM/DD/YYYY format.`;
  }

  const parts = dateStr.split("/");
  const month = Number.parseInt(parts[0], 10);
  const day = Number.parseInt(parts[1], 10);
  const year = Number.parseInt(parts[2], 10);

  if (month < 1 || month > 12) {
    return `Please enter a valid month (01-12) for the ${fieldLabel}.`;
  }

  if (year < 1900 || year > 2100) {
    return `Please enter a valid 4-digit year (1900-2100) for the ${fieldLabel}.`;
  }

  const maxDay = getDaysInMonth(month, year);

  if (day < 1 || day > maxDay) {
    return `Please enter a valid day (01-${maxDay}) for the selected month in ${fieldLabel}.`;
  }

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const enteredDate = new Date(year, month - 1, day);
  if (enteredDate <= today) {
    return `The ${fieldLabel} must be a future date (greater than today).`;
  }

  return null;
};

const buildRegistrationPayload = (
  memberData,
  opportunity,
  extraPayload,
  {
    phoneVal,
    emailVal,
    line1Val,
    line2Val,
    cityVal,
    stateVal,
    postalCodeVal,
    binVal,
  },
) => {
  const { firstName, lastName } = splitMemberName(memberData);
  const cleanPhone = (val) => String(val || "").replaceAll(/\D/g, "");

  return {
    ndcId: opportunity?.ndc || opportunity?.drugNdc || opportunity?.ndcId,
    dateOfBirth: normalizeDateOfBirth(memberData?.dob),
    firstName,
    lastName,
    phoneNumber: cleanPhone(extraPayload.phoneNumber || phoneVal),
    emailAddress: extraPayload.emailAddress || extraPayload.email || emailVal,
    gender: normalizeGender(memberData),
    groupId:
      memberData?.groupNum ||
      memberData?.groupId ||
      memberData?.groupNumber ||
      "",
    cardholderId:
      memberData?.subscriberNum ||
      memberData?.cardholderId ||
      memberData?.subscriberNumber ||
      "",
    addressOne: extraPayload.line1 || extraPayload.addressOne || line1Val,
    addressTwo: extraPayload.line2 || extraPayload.addressTwo || line2Val,
    city: extraPayload.city || cityVal,
    stateCode: extraPayload.state || extraPayload.stateCode || stateVal,
    postalCode: extraPayload.postalCode || postalCodeVal,
    pcn: extraPayload.pcn || "",
    bin: extraPayload.bin || binVal,
  };
};

const getPreCheckInputType = (field) => {
  if (field === "phoneNumber") return "tel";
  if (field === "email") return "email";
  return "text";
};

const getMockScenarioStatus = (copayScenario, regStatus) => {
  if (copayScenario === "scenario1" || copayScenario === "scenario2") {
    return "AUTOMATED";
  }
  if (copayScenario === "scenario3") return "MANUAL";
  return regStatus;
};

const isFilled = (value) =>
  value !== undefined && value !== null && value !== "";

const displayOrDash = (value) => (!value || value === "N/A" ? "-" : value);

const formatCurrency = (val) =>
  `$${Number(val).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const formatAnnualBenefitAmount = (amount) => {
  if (!isFilled(amount)) return "-";
  if (typeof amount === "number" || !Number.isNaN(Number(amount))) {
    return formatCurrency(amount);
  }
  return amount;
};

const formatPerFillAmount = (cardData, initialCardData, opportunity) => {
  const sources = [
    cardData?.perFillAmount,
    initialCardData?.perFillAmount,
    opportunity?.perFillAmount,
    opportunity?.program?.perFillAmount,
  ];
  const val = sources.find(isFilled) ?? null;
  if (val === null) return "$150.00";
  if (typeof val === "number" || !Number.isNaN(Number(val))) {
    return formatCurrency(val);
  }
  return String(val).startsWith("$") ? val : `$${val}`;
};

const formatExpirationDate = (dateVal) => {
  if (!dateVal || dateVal === "N/A" || dateVal === "-") {
    return "-";
  }
  const parts = String(dateVal).split("-");
  if (parts.length === 3 && parts[0].length === 4) {
    return `${parts[1]}/${parts[2]}/${parts[0]}`;
  }
  try {
    const d = new Date(dateVal);
    if (!Number.isNaN(d.getTime())) {
      return d.toLocaleDateString("en-US", {
        month: "2-digit",
        day: "2-digit",
        year: "numeric",
        timeZone: "UTC",
      });
    }
    return dateVal;
  } catch (e) {
    console.error("Error parsing date:", e);
    return dateVal;
  }
};

const buildCardDetailFields = (cardData, initialCardData, opportunity) => [
  { label: "IIN Number (RxBIN)", value: displayOrDash(cardData.bin) },
  { label: "Processor ID (RxPCN)", value: displayOrDash(cardData.pcn) },
  { label: "Group ID (RxGRP)", value: displayOrDash(cardData.grpId) },
  { label: "Card Holder ID", value: displayOrDash(cardData.memberId) },
  {
    label: "Annual Benefit Amount ($)",
    value: formatAnnualBenefitAmount(cardData.annualBenefitAmount),
  },
  {
    label: "Per Fill Amount ($)",
    isPerFill: true,
    value: formatPerFillAmount(cardData, initialCardData, opportunity),
  },
  {
    label: "Annual Benefit Expiration Date",
    value: formatExpirationDate(cardData.annualBenefitExpirationDate),
  },
];

const CopayCardModal = ({
  open,
  onClose,
  opportunity,
  memberData,
  onPersistSuccess,
  viewOnly = false,
  initialCardData = null,
  copayOpportunities = [],
  copayScenario = "scenario1",
  copayDetailsMap = {},
}) => {
  const [loading, setLoading] = useState(false);
  const [cardData, setCardData] = useState(null);
  const [error, setError] = useState(null);
  const [copiedField, setCopiedField] = useState(null);
  const [missingFieldsPreCheck, setMissingFieldsPreCheck] = useState([]);
  const [preCheckInputs, setPreCheckInputs] = useState({
    phoneNumber: "",
    email: "",
    line1: "",
    line2: "",
    city: "",
    state: "",
    postalCode: "",
    pcn: "",
    bin: "",
  });
  const [preCheckRequired, setPreCheckRequired] = useState(false);
  const pollingTimeoutRef = React.useRef(null);

  // Scenario Mock States
  const [needs2FA, setNeeds2FA] = useState(false);
  const [verificationCode, setVerificationCode] = useState("");
  const [verificationError, setVerificationError] = useState("");
  const [needsManual, setNeedsManual] = useState(false);
  const [isEditingPerFill, setIsEditingPerFill] = useState(false);
  const [perFillInputValue, setPerFillInputValue] = useState("");
  const [manualFields, setManualFields] = useState({
    bin: "",
    pcn: "",
    grpId: "",
    memberId: "",
    perFillAmount: "",
    annualValue: "",
    annualBenefitExpirationDate: "",
  });

  const initializeFromOpportunity = () => {
    const oppId = getOpportunityId(opportunity);
    const details = copayDetailsMap?.[oppId] || {};
    let regStatus =
      initialCardData?.registrationRequestStatus ||
      details?.copayRegistrationRequestStatus ||
      opportunity?.registrationRequestStatus ||
      "";

    const isMockOpportunity = !oppId || oppId === "mock-opp-id";

    // Override based on the selected demo switcher scenario ONLY for mock opportunities
    if (isMockOpportunity) {
      regStatus = getMockScenarioStatus(copayScenario, regStatus);
    }

    if (regStatus === "MANUAL") {
      setNeedsManual(true);
      setLoading(false);
      setError(
        `${opportunity?.drug || "Veozah 45 MG"} isn't enrolled in the automated copay card program. Secure the card directly with the manufacturer, then enter the details below.`,
      );
      if (initialCardData) {
        setManualFields({
          bin: initialCardData.bin || "",
          pcn: initialCardData.pcn || "",
          grpId: initialCardData.grpId || "",
          memberId: initialCardData.memberId || "",
          perFillAmount: formatManualPerFillAmount(
            initialCardData,
            opportunity,
          ),
          annualValue: formatManualAnnualValue(initialCardData),
          annualBenefitExpirationDate: formatToUsDate(
            initialCardData.annualBenefitExpirationDate,
          ),
        });
      }
    } else {
      void handleFetchCopayCard();
    }
  };

  const resetCardState = () => {
    // Clear polling timeout on close
    if (pollingTimeoutRef.current) {
      clearTimeout(pollingTimeoutRef.current);
      pollingTimeoutRef.current = null;
    }
    // Reset state on close
    setCardData(null);
    setError(null);
    setLoading(false);
    setMissingFieldsPreCheck([]);
    setPreCheckInputs({
      phoneNumber: "",
      email: "",
      line1: "",
      line2: "",
      city: "",
      state: "",
      postalCode: "",
      pcn: "",
      bin: "",
    });
    setPreCheckRequired(false);

    // Reset mock states
    setNeeds2FA(false);
    setVerificationCode("");
    setVerificationError("");
    setNeedsManual(false);
    setIsEditingPerFill(false);
    setPerFillInputValue("");
    setManualFields({
      bin: "",
      pcn: "",
      grpId: "",
      memberId: "",
      perFillAmount: "",
      annualValue: "",
      annualBenefitExpirationDate: "",
    });
  };

  useEffect(() => {
    setIsEditingPerFill(false);
    setPerFillInputValue("");

    if (open && opportunity) {
      if (viewOnly && initialCardData) {
        setCardData(initialCardData);
        setLoading(false);
      } else {
        initializeFromOpportunity();
      }
    } else {
      resetCardState();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, opportunity, viewOnly, initialCardData]);

  const handleSaveInlinePerFill = async () => {
    const numVal =
      Number.parseFloat(String(perFillInputValue).replaceAll(/[^0-9.]/g, "")) ||
      0;
    const updatedCard = {
      ...cardData,
      perFillAmount: numVal,
    };
    setCardData(updatedCard);
    setIsEditingPerFill(false);

    if (onPersistSuccess) {
      onPersistSuccess(updatedCard);
    }

    const mId = memberData?.id || memberData?.memberId;
    const oppId = getOpportunityId(opportunity);

    if (mId && oppId) {
      try {
        await updateProgramDetails(
          mId,
          oppId,
          cardData?.annualBenefitAmount || null,
          cardData?.memberSavings || null,
          numVal,
        );
      } catch (e) {
        console.error(
          "Failed to update program details for per fill amount:",
          e,
        );
      }
    }
  };

  const getEffectiveRegUrl = () => {
    const oppId = getOpportunityId(opportunity);
    const details = copayDetailsMap?.[oppId] || {};

    const rawUrl =
      opportunity?.regUrl ||
      opportunity?.copayRegUrl ||
      opportunity?.program?.regUrl ||
      opportunity?.program?.copayRegUrl ||
      cardData?.regUrl ||
      cardData?.copayRegUrl ||
      initialCardData?.regUrl ||
      initialCardData?.copayRegUrl ||
      details?.regUrl ||
      details?.copayRegUrl ||
      opportunity?.manufacturerUrl ||
      opportunity?.websiteUrl ||
      opportunity?.url;
    console.log(rawUrl);
    if (rawUrl) return rawUrl;

    const drugName = (opportunity?.drug || "")
      .trim()
      .split(" ")[0]
      .toLowerCase();
    if (drugName === "vraylar")
      return "https://www.vraylar.com/cost-and-savings";
    if (drugName === "mounjaro") return "https://mounjaro.lilly.com/";
    if (drugName === "tezspire")
      return "https://www.tezspire.com/copay-savings-card";
    if (drugName === "nurtec") return "https://www.nurtec.com/savings";
    if (drugName) return `https://www.${drugName}.com`;

    return "";
  };

  const completeMockScenario1 = () => {
    if (!open) return;
    const mockCard = {
      bin: "004682",
      pcn: "CN",
      grpId: "EC40401040",
      memberId: "09843046027",
      annualBenefitAmount: 7000,
      annualValue: "$7,000.00",
      perFillAmount: 150,
      annualBenefitExpirationDate: "2026-12-31",
      executionStatus: "SUCCESS",
      registrationRequestStatus: "AUTOMATED",
      notes: "This drug is automated and currently active in production.",
    };
    setCardData(mockCard);
    setLoading(false);
    if (onPersistSuccess) {
      onPersistSuccess(mockCard);
    }
  };

  const completeMockScenario2 = () => {
    if (!open) return;
    setLoading(false);
    setNeeds2FA(true);
  };

  const runMockScenario = (scenario) => {
    if (!open) return;
    if (scenario === "scenario1") {
      setTimeout(completeMockScenario1, 1500);
    } else if (scenario === "scenario2") {
      setTimeout(completeMockScenario2, 1200);
    }
  };

  const handleFetchCopayCard = async (
    extraPayload = {},
    bypassPreCheck = false,
  ) => {
    setLoading(true);
    setError(null);
    setCardData(null);
    setNeeds2FA(false);
    setNeedsManual(false);

    const oppRef = getOpportunityId(opportunity);

    const isMockOpportunity = !oppRef || oppRef === "mock-opp-id";
    const regStatus =
      initialCardData?.registrationRequestStatus ||
      opportunity?.registrationRequestStatus ||
      "";

    if (bypassPreCheck) {
      if (
        regStatus === "MANUAL" ||
        (isMockOpportunity && copayScenario === "scenario3")
      ) {
        setLoading(false);
        setNeedsManual(true);
        setError(
          `${opportunity?.drug || "-"} isn't enrolled in the automated copay card program. Secure the card directly with the manufacturer, then enter the details below.`,
        );
        return;
      }

      if (isMockOpportunity && copayScenario) {
        setTimeout(() => runMockScenario(copayScenario), 800);
        return;
      }
    }

    try {
      // Gather current or overridden values
      const phoneVal =
        memberData?.phoneNumber ||
        memberData?.phone ||
        memberData?.preferredPhoneNum ||
        memberData?.preferredPhoneNumber ||
        memberData?.alternatePhoneNum ||
        memberData?.alternatePhoneNumber ||
        "";

      const emailVal =
        memberData?.email ||
        memberData?.alternateEmailAddress ||
        memberData?.alternateEmail ||
        "";

      const addr = memberData?.address || {};
      const line1Val = addr?.line1 || "";
      const line2Val = addr?.line2 || "";
      const cityVal = addr?.city || "";
      const stateVal = addr?.state || "";
      const postalCodeVal = addr?.postalCode || "";

      // Extract PCN and BIN from the opportunity program
      const pcnVal = opportunity?.program?.pcn || opportunity?.pcn || "";
      const binVal = opportunity?.program?.bin || opportunity?.bin || "";

      if (!bypassPreCheck) {
        const hasAllFields = false;

        if (!hasAllFields) {
          setMissingFieldsPreCheck([
            "phoneNumber",
            "email",
            "line1",
            "city",
            "state",
            "postalCode",
            "pcn",
            "bin",
          ]);
          setPreCheckInputs({
            phoneNumber: phoneVal,
            email: emailVal,
            line1: line1Val,
            line2: line2Val,
            city: cityVal,
            state: stateVal,
            postalCode: postalCodeVal,
            pcn: pcnVal,
            bin: binVal,
          });
          setPreCheckRequired(true);
          setLoading(false);
          return;
        }
      }

      const registrationPayload = buildRegistrationPayload(
        memberData,
        opportunity,
        extraPayload,
        {
          phoneVal,
          emailVal,
          line1Val,
          line2Val,
          cityVal,
          stateVal,
          postalCodeVal,
          binVal,
        },
      );

      console.log(
        "Submitting Aedificare registration request payload:",
        registrationPayload,
      );

      void handlePollTick(oppRef, registrationPayload);
    } catch (err) {
      console.error("Aedificare Copay Finder API failed:", err);
      setError(parseAedificareError(err));
      setLoading(false);
      if (onPersistSuccess) {
        onPersistSuccess({ executionStatus: "FAILED" });
      }
    }
  };

  const parseAedificareError = (err) => {
    let displayError =
      err?.message || "Status check failed during copay card registration.";
    const serverData = err?.response?.data;
    if (serverData?.message) {
      const rawMessage = serverData.message;
      const jsonStartIdx = rawMessage.indexOf("{");
      if (jsonStartIdx > -1) {
        try {
          const jsonStr = rawMessage.substring(jsonStartIdx);
          const parsed = JSON.parse(jsonStr);
          const innerMessage =
            parsed?._embedded?.errors?.[0]?.message || parsed?.message;
          if (innerMessage) {
            displayError = innerMessage;
          }
        } catch (e) {
          console.error("Failed to parse inner Aedificare error JSON:", e);
          displayError = serverData.message;
        }
      } else {
        displayError = serverData.message;
      }
    }
    return displayError;
  };

  const handlePollTick = async (
    reqId,
    payloadOrRequestId,
    isPolling = false,
  ) => {
    if (!open) return;

    try {
      let statusRes;
      if (isPolling) {
        statusRes = await pollCopayCardStatus(payloadOrRequestId);
      } else {
        statusRes = await requestCopayCard(reqId, payloadOrRequestId);
      }

      const status = statusRes.executionStatus;
      const requestId = statusRes.registrationRequestId || payloadOrRequestId;

      if (status === "PENDING") {
        if (pollingTimeoutRef.current) {
          clearTimeout(pollingTimeoutRef.current);
        }
        pollingTimeoutRef.current = setTimeout(() => {
          void handlePollTick(reqId, requestId, true);
        }, 3000);
      } else if (
        status === "SUCCESS" ||
        status === "COMPLETE" ||
        (statusRes.bin && statusRes.memberId)
      ) {
        setCardData(statusRes);
        setLoading(false);
        onPersistSuccess?.(statusRes);
      } else {
        console.error(`Aedificare polling failed with status ${status}`);
        setError(
          `Copay card registration request failed with status: ${status}.`,
        );
        setLoading(false);
        onPersistSuccess?.({ executionStatus: "FAILED" });
      }
    } catch (err) {
      console.error("Aedificare status check failed:", err);
      setError(parseAedificareError(err));
      setLoading(false);
      onPersistSuccess?.({ executionStatus: "FAILED" });
    }
  };

  const copyToClipboard = (text, fieldName) => {
    if (!text) return;
    navigator.clipboard.writeText(text).catch(() => setCopiedField(null));
    setCopiedField(fieldName);
    setTimeout(() => {
      setCopiedField(null);
    }, 1500);
  };

  const handleSubmitPreCheck = () => {
    const payload = {};
    const isMissing = (field) => missingFieldsPreCheck.includes(field);
    const pick = (field) =>
      isMissing(field) ? preCheckInputs[field] : undefined;

    if (isMissing("phoneNumber")) {
      payload.phoneNumber = preCheckInputs.phoneNumber;
    }
    if (isMissing("email")) {
      payload.emailAddress = preCheckInputs.email;
    }

    const addressFields = ["line1", "line2", "city", "state", "postalCode"];

    if (addressFields.some(isMissing)) {
      payload.address = {
        line1: pick("line1"),
        line2: pick("line2"),
        city: pick("city"),
        state: pick("state"),
        postalCode: pick("postalCode"),
      };

      addressFields.filter(isMissing).forEach((field) => {
        payload[field] = preCheckInputs[field];
      });
      if (isMissing("line2")) {
        payload.addressTwo = preCheckInputs.line2;
      }
    }

    if (isMissing("pcn")) {
      payload.pcn = preCheckInputs.pcn;
    }
    if (isMissing("bin")) {
      payload.bin = preCheckInputs.bin;
    }
    setPreCheckRequired(false);
    void handleFetchCopayCard(payload, true);
  };

  const isPhoneValid = (phone) =>
    /^\d{10}$/.test(String(phone || "").replaceAll(/\D/g, ""));
  const isEmailValid = (email) =>
    /^[^\s@]+@[^\s@.]+(?:\.[^\s@.]+)+$/.test(String(email || "").trim());

  const handlePreCheckInputChange = (field, e) => {
    let val = e.target.value;
    if (field === "phoneNumber") {
      val = val.replaceAll(/\D/g, "").slice(0, 10);
    }
    setPreCheckInputs((prev) => ({
      ...prev,
      [field]: val,
    }));
  };

  const getPreCheckHelperText = (field) => {
    if (
      field === "phoneNumber" &&
      !!preCheckInputs.phoneNumber &&
      !isPhoneValid(preCheckInputs.phoneNumber)
    ) {
      return "Must be a 10-digit number";
    }
    if (
      field === "email" &&
      !!preCheckInputs.email &&
      !isEmailValid(preCheckInputs.email)
    ) {
      return "Invalid email address";
    }
    return "";
  };

  const isPreCheckSubmitDisabled = missingFieldsPreCheck.some((field) => {
    const val = preCheckInputs[field]?.trim();
    if (!val) return true;
    if (field === "phoneNumber") {
      return !isPhoneValid(val);
    }
    if (field === "email") {
      return !isEmailValid(val);
    }
    return false;
  });

  const handleVerify2FA = () => {
    if (verificationCode.length !== 6) {
      setVerificationError("Code must be 6 digits.");
      return;
    }
    setLoading(true);
    setNeeds2FA(false);
    setTimeout(() => {
      if (!open) return;
      const mockCard = {
        bin: "610649",
        pcn: "LUCYRX01",
        grpId: "MTU-4471",
        memberId: "9004 7723 5510",
        expirationDate: "2026-12-31",
        annualValue: "$13,500",
        executionStatus: "SUCCESS",
      };
      setCardData(mockCard);
      setLoading(false);
      if (onPersistSuccess) {
        onPersistSuccess(mockCard);
      }
    }, 1200);
  };

  const handleSaveManualDetails = async () => {
    setLoading(true);
    setError(null);

    const oppRef = getOpportunityId(opportunity) || "mock-opp-id";

    const annualExpDateErr = validateDateComponents(
      manualFields.annualBenefitExpirationDate,
      "annual expiration date",
    );
    if (annualExpDateErr) {
      setError(annualExpDateErr);
      setLoading(false);
      return;
    }

    const isoAnnualExpDate = formatToIso(
      manualFields.annualBenefitExpirationDate,
    );

    const rawAnnualValue =
      Number.parseFloat(
        String(manualFields.annualValue || "$13,500").replaceAll(
          /[^0-9.]/g,
          "",
        ),
      ) || 13500;

    const rawPerFillAmount =
      Number.parseFloat(
        String(manualFields.perFillAmount || "$150").replaceAll(/[^0-9.]/g, ""),
      ) || 150;

    const payload = {
      bin: manualFields.bin || "610649",
      pcn: manualFields.pcn || "LUCYRX01",
      groupId: manualFields.grpId || "MTU-4471",
      cardholderId: manualFields.memberId || "9004 7723 5510",
      annualBenefitAmount: rawAnnualValue,
      perFillAmount: rawPerFillAmount,
      annualBenefitExpirationDate: isoAnnualExpDate,
    };

    try {
      await submitManualCopayCard(oppRef, payload);

      const mockCard = {
        bin: payload.bin,
        pcn: payload.pcn,
        grpId: payload.groupId,
        memberId: payload.cardholderId,
        perFillAmount: payload.perFillAmount,
        annualValue:
          manualFields.annualValue || `$${rawAnnualValue.toLocaleString()}`,
        annualBenefitAmount: payload.annualBenefitAmount,
        annualBenefitExpirationDate: payload.annualBenefitExpirationDate,
        executionStatus: "MANUAL",
        registrationRequestStatus: "MANUAL",
        notes:
          "This drug isn't currently enrolled in Aedificare's automated copay program. Card details were entered manually and should be re-verified at next fill.",
      };

      setCardData(mockCard);
      setNeedsManual(false);
      setError(null);
      if (onPersistSuccess) {
        onPersistSuccess(mockCard);
      }
    } catch (err) {
      const errMsg =
        err.response?.data?.message ||
        err.message ||
        "Failed to submit manual copay card details.";
      setError(errMsg);
    } finally {
      setLoading(false);
    }
  };

  let modalTitle = "Copay Finder";
  let statusBadge = null;
  let titleIcon = <CreditCardIcon sx={{ color: "#403C6F" }} />;

  if (needsManual || cardData?.executionStatus === "MANUAL") {
    modalTitle = "Manual Copay Card Entry";
    titleIcon = (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          width: "28px",
          height: "28px",
          borderRadius: "50%",
          backgroundColor: "#FFF9E6",
          color: "#B78103",
        }}
      >
        <HandIcon size={16} />
      </Box>
    );
    statusBadge = (
      <Box
        sx={{
          px: 1.5,
          py: 0.4,
          borderRadius: "12px",
          backgroundColor: "#FFF9E6",
          border: "1px solid #FFE49E",
          color: "#B78103",
          fontSize: "11px",
          fontWeight: 700,
          letterSpacing: "0.5px",
        }}
      >
        MANUAL
      </Box>
    );
  } else if (
    cardData &&
    (cardData.executionStatus === "SUCCESS" ||
      cardData.executionStatus === "COMPLETE")
  ) {
    modalTitle = "Copay Card Details";
    titleIcon = null;
    statusBadge = (
      <Box
        sx={{
          px: 1.5,
          py: 0.4,
          borderRadius: "12px",
          backgroundColor: "#EBFCEE",
          border: "1px solid #C3F2CB",
          color: "#2D9D42",
          fontSize: "11px",
          fontWeight: 700,
          letterSpacing: "0.5px",
        }}
      >
        SUCCESS
      </Box>
    );
  } else if (needs2FA) {
    modalTitle = "Identity Verification";
  }

  const isManualMode =
    needsManual || cardData?.registrationRequestStatus === "MANUAL";

  return (
    <Dialog
      open={open}
      onClose={loading ? undefined : onClose}
      maxWidth="xs"
      fullWidth
      slotProps={{
        paper: {
          sx: {
            borderRadius: "20px",
            padding: "8px",
            backgroundColor: "#FFFFFF",
            boxShadow: "0px 10px 30px rgba(0, 0, 0, 0.08)",
          },
        },
      }}
    >
      {isManualMode ? (
        <Box
          sx={{
            display: "flex",
            gap: 1.5,
            px: 2,
            pt: 1.5,
            alignItems: "flex-start",
            width: "100%",
          }}
        >
          <Box
            sx={{
              flexGrow: 1,
              display: "flex",
              flexDirection: "column",
              gap: 0.25,
              minWidth: 0,
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                width: "100%",
                gap: 1,
              }}
            >
              <Typography
                sx={{ fontWeight: 700, fontSize: "16px", color: "#10100F" }}
              >
                Manual Copay Card Entry
              </Typography>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  flexShrink: 0,
                }}
              >
                {statusBadge}
                <IconButton
                  onClick={onClose}
                  disabled={loading}
                  size="small"
                  sx={{ color: "#6E6D92" }}
                >
                  <CloseIcon sx={{ fontSize: "18px" }} />
                </IconButton>
              </Box>
            </Box>
            <Typography
              sx={{
                fontSize: "12px",
                color: "#6E6D92",
                fontWeight: 400,
                textAlign: "left",
                lineHeight: 1.4,
              }}
            >
              This copay card cannot be issued automatically through the system.
              Please secure the card directly from the manufacturer and record
              the details below.
            </Typography>
          </Box>
        </Box>
      ) : (
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            px: 2,
            pt: 1.5,
          }}
        >
          <Typography
            sx={{
              fontWeight: 700,
              fontSize: "18px",
              color: "#10100F",
              display: "flex",
              alignItems: "center",
              gap: 1.5,
            }}
          >
            {titleIcon} {modalTitle}
          </Typography>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            {statusBadge}
            <IconButton
              onClick={onClose}
              disabled={loading}
              size="small"
              sx={{ color: "#6E6D92" }}
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </Box>
      )}

      <DialogContent
        sx={{
          p: 2,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          textAlign: "center",
        }}
      >
        {loading && (
          <Box
            sx={{
              py: 6,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 2,
            }}
          >
            <CircularProgress size={50} sx={{ color: "#403C6F" }} />
            <Typography
              sx={{ fontWeight: 600, fontSize: "16px", color: "#403C6F" }}
            >
              Retrieving Copay Card
            </Typography>
            {/* <Typography sx={{ fontSize: "14px", color: "#6E6D92" }}>
              {pollingStatus}
            </Typography> */}
          </Box>
        )}

        {preCheckRequired && !needsManual && !needs2FA && !loading && (
          <Box
            sx={{
              py: 2,
              px: 2,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 2,
              width: "100%",
            }}
          >
            <Box
              sx={{
                width: "100%",
                maxWidth: "440px",
                display: "flex",
                flexDirection: "column",
                gap: 2,
                textAlign: "left",
              }}
            >
              {[
                ["phoneNumber", "email"],
                ["line1"],
                ["line2"],
                ["city", "state"],
                ["postalCode"],
                ["pcn", "bin"],
              ].map((rowFields) => {
                const activeFields = rowFields.filter((f) =>
                  missingFieldsPreCheck.includes(f),
                );
                if (activeFields.length === 0) return null;

                return (
                  <Box
                    key={rowFields.join("-")}
                    sx={{ display: "flex", gap: 2, width: "100%" }}
                  >
                    {activeFields.map((field) => (
                      <Box
                        key={field}
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          gap: 0.5,
                          flex: 1,
                        }}
                      >
                        <Typography
                          sx={{
                            fontSize: "13px",
                            color: "#403C6F",
                            fontWeight: 600,
                          }}
                        >
                          {FIELD_LABELS[field]}{" "}
                          <span style={{ color: "#E05353" }}>*</span>
                        </Typography>
                        <TextField
                          fullWidth
                          variant="outlined"
                          size="small"
                          placeholder={`Enter ${FIELD_LABELS[field]}`}
                          type={getPreCheckInputType(field)}
                          value={preCheckInputs[field] || ""}
                          onChange={(e) => handlePreCheckInputChange(field, e)}
                          error={
                            (field === "phoneNumber" &&
                              !!preCheckInputs.phoneNumber &&
                              !isPhoneValid(preCheckInputs.phoneNumber)) ||
                            (field === "email" &&
                              !!preCheckInputs.email &&
                              !isEmailValid(preCheckInputs.email))
                          }
                          helperText={getPreCheckHelperText(field)}
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              borderRadius: "10px",
                              backgroundColor: "#F7F8FB",
                            },
                          }}
                        />
                      </Box>
                    ))}
                  </Box>
                );
              })}
            </Box>

            <Box sx={{ display: "flex", gap: 2, mt: 2 }}>
              <Button
                variant="contained"
                onClick={handleSubmitPreCheck}
                disabled={isPreCheckSubmitDisabled}
                sx={{
                  borderRadius: "50px",
                  textTransform: "none",
                  fontWeight: 600,
                  backgroundColor: "#403C6F",
                  color: "#FFF",
                  px: 3,
                  "&:hover": { backgroundColor: "#312e5e" },
                  "&.Mui-disabled": {
                    backgroundColor: "#C6C5D3",
                    color: "#FFF",
                  },
                }}
              >
                Submit & Request Card
              </Button>
            </Box>
          </Box>
        )}

        {error && !needsManual && !needs2FA && !loading && (
          <Box
            sx={{
              py: 4,
              px: 2,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 2,
              width: "100%",
            }}
          >
            <WarningIcon sx={{ color: "#E05353", fontSize: "64px" }} />
            <Typography
              sx={{
                fontWeight: 700,
                fontSize: "18px",
                color: "#E05353",
                textAlign: "center",
              }}
            >
              Unable to Retrieve Copay Card
            </Typography>
            <Typography
              sx={{
                fontSize: "14px",
                color: "#E05353",
                maxWidth: "90%",
                mt: 1,
                textAlign: "center",
                lineHeight: 1.5,
              }}
            >
              {error}
            </Typography>

            <Box sx={{ display: "flex", gap: 2, mt: 2 }}>
              <Button
                variant="contained"
                onClick={handleFetchCopayCard}
                sx={{
                  borderRadius: "50px",
                  textTransform: "none",
                  fontWeight: 600,
                  backgroundColor: "#403C6F",
                  color: "#FFF",
                  px: 3,
                  "&:hover": { backgroundColor: "#312e5e" },
                }}
              >
                Retry Request
              </Button>
            </Box>
          </Box>
        )}

        {needs2FA && !loading && (
          <Box
            sx={{
              py: 2,
              px: 2,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 2.5,
              width: "100%",
            }}
          >
            <Typography
              sx={{
                fontSize: "14px",
                color: "#6E6D92",
                textAlign: "center",
                mb: 1,
              }}
            >
              Enter the 6-digit security code sent to the member's mobile
              device.
            </Typography>
            <TextField
              variant="outlined"
              size="small"
              placeholder="000 000"
              value={verificationCode}
              onChange={(e) => {
                setVerificationCode(
                  e.target.value.replaceAll(/\D/g, "").slice(0, 6),
                );
                setVerificationError("");
              }}
              error={!!verificationError}
              helperText={verificationError}
              slotProps={{
                htmlInput: {
                  style: {
                    textAlign: "center",
                    letterSpacing: "4px",
                    fontSize: "18px",
                    fontWeight: "bold",
                  },
                },
              }}
              sx={{
                width: "180px",
                "& .MuiOutlinedInput-root": {
                  borderRadius: "10px",
                  backgroundColor: "#F7F8FB",
                },
              }}
            />
            {verificationError && (
              <Typography sx={{ fontSize: "12px", color: "#E05353" }}>
                {verificationError}
              </Typography>
            )}
            <Button
              variant="contained"
              onClick={handleVerify2FA}
              disabled={verificationCode.length !== 6}
              sx={{
                borderRadius: "50px",
                textTransform: "none",
                fontWeight: 600,
                backgroundColor: "#403C6F",
                color: "#FFF",
                px: 4,
                py: 1,
                "&:hover": { backgroundColor: "#312e5e" },
                "&.Mui-disabled": {
                  backgroundColor: "#C6C5D3",
                  color: "#FFF",
                },
              }}
            >
              Verify Code
            </Button>
          </Box>
        )}

        {needsManual && !loading && (
          <Box
            sx={{
              py: 1,
              px: 1,
              display: "flex",
              flexDirection: "column",
              width: "100%",
            }}
          >
            {/* Warning banner */}
            <Alert
              severity="warning"
              variant="outlined"
              icon={<WarningIcon sx={{ color: "#B78103", fontSize: "20px" }} />}
              sx={{
                mb: 2.5,
                backgroundColor: "#FFF9E6",
                boxShadow: "0px 0px 2px 0px #00000040",
                border: "1px solid #FFE49E",
                borderRadius: "12px",
                color: "#6E6D92",
                fontWeight: 500,
                alignItems: "flex-start",
                textAlign: "left",
                "& .MuiAlert-icon": {
                  pt: 0.5,
                  mr: 1.5,
                },
                "& .MuiAlert-message": {
                  p: 0,
                  fontSize: "13px",
                  lineHeight: 1.5,
                },
              }}
            >
              <Typography variant="body2" component="div">
                {opportunity?.drug || "Vraylar"} isn't enrolled in the automated
                copay card program. Secure the card directly with the
                manufacturer, then enter the details below.
              </Typography>
              {getEffectiveRegUrl() && (
                <Box sx={{ mt: 1 }}>
                  <Typography
                    component="a"
                    href={
                      getEffectiveRegUrl().startsWith("http")
                        ? getEffectiveRegUrl()
                        : `https://${getEffectiveRegUrl()}`
                    }
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{
                      color: "#2B6CB0",
                      fontSize: "12px",
                      fontWeight: 600,
                      textDecoration: "underline",
                      wordBreak: "break-all",
                    }}
                  >
                    {getEffectiveRegUrl()}
                  </Typography>
                </Box>
              )}
            </Alert>

            <Typography
              sx={{
                fontSize: "11px",
                fontWeight: 700,
                color: "#7F7C9F",
                letterSpacing: "0.5px",
                mb: 2,
                textAlign: "left",
                textTransform: "none",
              }}
            >
              Enter Card Details
            </Typography>

            {/* Input Form Fields */}
            <Box
              sx={{
                width: "100%",
                display: "flex",
                flexDirection: "column",
                gap: 2,
              }}
            >
              <Box sx={{ display: "flex", gap: 2 }}>
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 0.5,
                    flex: 1,
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: "11px",
                      fontWeight: 600,
                      color: "#7F7C9F",
                      textAlign: "left",
                      textTransform: "none",
                    }}
                  >
                    IIN Number (RxBIN)
                  </Typography>
                  <TextField
                    fullWidth
                    variant="outlined"
                    size="small"
                    placeholder="e.g. 610649"
                    value={manualFields.bin}
                    onChange={(e) =>
                      setManualFields({ ...manualFields, bin: e.target.value })
                    }
                    slotProps={{
                      htmlInput: { maxLength: 8 },
                      input: {
                        endAdornment: (
                          <InputAdornment position="end">
                            <Typography
                              sx={{ fontSize: "11px", color: "#7F7C9F" }}
                            >
                              {String(manualFields.bin || "").length}/8
                            </Typography>
                          </InputAdornment>
                        ),
                      },
                    }}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: "8px",
                        backgroundColor: "#FFFFFF",
                      },
                    }}
                  />
                </Box>
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 0.5,
                    flex: 1,
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: "11px",
                      fontWeight: 600,
                      color: "#7F7C9F",
                      textAlign: "left",
                      textTransform: "none",
                    }}
                  >
                    Processor ID (RxPCN)
                  </Typography>
                  <TextField
                    fullWidth
                    variant="outlined"
                    size="small"
                    placeholder="e.g. LUCYRX01"
                    value={manualFields.pcn}
                    onChange={(e) =>
                      setManualFields({ ...manualFields, pcn: e.target.value })
                    }
                    slotProps={{
                      htmlInput: { maxLength: 15 },
                      input: {
                        endAdornment: (
                          <InputAdornment position="end">
                            <Typography
                              sx={{ fontSize: "11px", color: "#7F7C9F" }}
                            >
                              {String(manualFields.pcn || "").length}/15
                            </Typography>
                          </InputAdornment>
                        ),
                      },
                    }}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: "8px",
                        backgroundColor: "#FFFFFF",
                      },
                    }}
                  />
                </Box>
              </Box>

              <Box sx={{ display: "flex", gap: 2 }}>
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 0.5,
                    flex: 1,
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: "11px",
                      fontWeight: 600,
                      color: "#7F7C9F",
                      textAlign: "left",
                      textTransform: "none",
                    }}
                  >
                    Group ID (RxGRP)
                  </Typography>
                  <TextField
                    fullWidth
                    variant="outlined"
                    size="small"
                    placeholder="e.g. MTU-4471"
                    value={manualFields.grpId}
                    onChange={(e) =>
                      setManualFields({
                        ...manualFields,
                        grpId: e.target.value,
                      })
                    }
                    slotProps={{
                      htmlInput: { maxLength: 20 },
                      input: {
                        endAdornment: (
                          <InputAdornment position="end">
                            <Typography
                              sx={{ fontSize: "11px", color: "#7F7C9F" }}
                            >
                              {String(manualFields.grpId || "").length}/20
                            </Typography>
                          </InputAdornment>
                        ),
                      },
                    }}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: "8px",
                        backgroundColor: "#FFFFFF",
                      },
                    }}
                  />
                </Box>
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 0.5,
                    flex: 1,
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: "11px",
                      fontWeight: 600,
                      color: "#7F7C9F",
                      textAlign: "left",
                      textTransform: "none",
                    }}
                  >
                    Card Holder ID
                  </Typography>
                  <TextField
                    fullWidth
                    variant="outlined"
                    size="small"
                    placeholder="e.g. 9004 7723 5510"
                    value={manualFields.memberId}
                    onChange={(e) =>
                      setManualFields({
                        ...manualFields,
                        memberId: e.target.value,
                      })
                    }
                    slotProps={{
                      htmlInput: { maxLength: 20 },
                      input: {
                        endAdornment: (
                          <InputAdornment position="end">
                            <Typography
                              sx={{ fontSize: "11px", color: "#7F7C9F" }}
                            >
                              {String(manualFields.memberId || "").length}/20
                            </Typography>
                          </InputAdornment>
                        ),
                      },
                    }}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: "8px",
                        backgroundColor: "#FFFFFF",
                      },
                    }}
                  />
                </Box>
              </Box>

              <Box sx={{ display: "flex", gap: 2 }}>
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 0.5,
                    flex: 1,
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: "11px",
                      fontWeight: 600,
                      color: "#7F7C9F",
                      textAlign: "left",
                      textTransform: "none",
                    }}
                  >
                    Annual Benefit Amount ($)
                  </Typography>
                  <TextField
                    fullWidth
                    variant="outlined"
                    size="small"
                    placeholder="e.g. $13,500"
                    value={manualFields.annualValue}
                    onChange={(e) =>
                      setManualFields({
                        ...manualFields,
                        annualValue: e.target.value,
                      })
                    }
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: "8px",
                        backgroundColor: "#FFFFFF",
                      },
                    }}
                  />
                </Box>
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 0.5,
                    flex: 1,
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: "11px",
                      fontWeight: 600,
                      color: "#7F7C9F",
                      textAlign: "left",
                      textTransform: "none",
                    }}
                  >
                    Per Fill Amount ($)
                  </Typography>
                  <TextField
                    fullWidth
                    variant="outlined"
                    size="small"
                    placeholder="e.g. $150"
                    value={manualFields.perFillAmount}
                    onChange={(e) =>
                      setManualFields({
                        ...manualFields,
                        perFillAmount: e.target.value,
                      })
                    }
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: "8px",
                        backgroundColor: "#FFFFFF",
                      },
                    }}
                  />
                </Box>
              </Box>

              <Box sx={{ display: "flex", gap: 2 }}>
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 0.5,
                    flex: 1,
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: "11px",
                      fontWeight: 600,
                      color: "#7F7C9F",
                      textAlign: "left",
                      textTransform: "none",
                    }}
                  >
                    Annual Expiration Date
                  </Typography>
                  <TextField
                    fullWidth
                    variant="outlined"
                    size="small"
                    placeholder="MM/DD/YYYY"
                    value={manualFields.annualBenefitExpirationDate}
                    slotProps={{ htmlInput: { maxLength: 10 } }}
                    onChange={(e) => {
                      const formatted = formatDateInput(
                        e.target.value,
                        manualFields.annualBenefitExpirationDate,
                      );
                      setManualFields({
                        ...manualFields,
                        annualBenefitExpirationDate: formatted,
                      });
                    }}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: "8px",
                        backgroundColor: "#FFFFFF",
                      },
                    }}
                  />
                </Box>
                <Box sx={{ flex: 1 }} />
              </Box>
            </Box>

            {/* Bottom Actions */}
            <Box sx={{ display: "flex", gap: 2, mt: 4, width: "100%" }}>
              <Button
                variant="outlined"
                onClick={onClose}
                sx={{
                  flex: 1,
                  borderRadius: "8px",
                  borderColor: "#E8E8E9",
                  color: "#6E6D92",
                  textTransform: "none",
                  fontWeight: 600,
                  "&:hover": {
                    borderColor: "#6E6D92",
                    backgroundColor: "rgba(110, 109, 146, 0.08)",
                  },
                }}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={handleSaveManualDetails}
                disabled={
                  loading ||
                  !isValidDateString(manualFields.annualBenefitExpirationDate)
                }
                sx={{
                  flex: 1,
                  borderRadius: "8px",
                  backgroundColor: "#403C6F",
                  color: "#FFFFFF",
                  textTransform: "none",
                  fontWeight: 600,
                  "&:hover": {
                    backgroundColor: "#312e5e",
                  },
                }}
              >
                Save card details
              </Button>
            </Box>
          </Box>
        )}

        {cardData && !needsManual && !needs2FA && !loading && (
          <Box sx={{ width: "100%", py: 1 }}>
            {/* Visual Copay Card */}

            <Box sx={valueContainerStyle}>
              {buildCardDetailFields(
                cardData,
                initialCardData,
                opportunity,
              ).map((field, index, arr) => (
                <Box
                  key={field.label}
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    py: 1.25,
                    borderBottom:
                      index === arr.length - 1 ? "none" : "1px solid #F0F0F3",
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1.5,
                      flex: 1,
                      minWidth: 0,
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: "13px",
                        color: "#6E6D92",
                        fontWeight: 500,
                        width:
                          field.label.startsWith("Annual Benefit") ||
                          field.label.startsWith("Per Fill")
                            ? "200px"
                            : "110px",
                        flexShrink: 0,
                        textAlign: "left",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {field.label}
                    </Typography>
                  </Box>
                  {field.isPerFill && isEditingPerFill ? (
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 0.5,
                        ml: "auto",
                      }}
                    >
                      <TextField
                        size="small"
                        autoFocus
                        value={perFillInputValue}
                        onChange={(e) => {
                          const val = e.target.value;
                          if (val === "" || /^\d*(\.\d{0,2})?$/.test(val)) {
                            setPerFillInputValue(val);
                          }
                        }}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            void handleSaveInlinePerFill();
                          } else if (e.key === "Escape") {
                            setIsEditingPerFill(false);
                          }
                        }}
                        slotProps={{
                          input: {
                            startAdornment: (
                              <InputAdornment
                                position="start"
                                sx={{ mr: 0.25 }}
                              >
                                <Typography
                                  sx={{
                                    fontSize: "13px",
                                    fontWeight: 600,
                                    color: "#10100F",
                                  }}
                                >
                                  $
                                </Typography>
                              </InputAdornment>
                            ),
                          },
                        }}
                        sx={{
                          width: "100px",
                          "& .MuiOutlinedInput-root": {
                            borderRadius: "8px",
                            backgroundColor: "#FFFFFF",
                            height: "32px",
                            fontSize: "13px",
                            fontWeight: 600,
                            px: 1,
                          },
                          "& .MuiOutlinedInput-input": {
                            padding: "4px 0",
                            textAlign: "right",
                          },
                        }}
                      />
                      <IconButton
                        size="small"
                        onClick={handleSaveInlinePerFill}
                        sx={{
                          width: "28px",
                          height: "28px",
                          backgroundColor: "#EBFCEE",
                          color: "#2D9D42",
                          borderRadius: "8px",
                          p: 0.5,
                          "&:hover": {
                            backgroundColor: "#C3F2CB",
                          },
                        }}
                      >
                        <CheckIcon sx={{ fontSize: "16px" }} />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => setIsEditingPerFill(false)}
                        sx={{
                          width: "28px",
                          height: "28px",
                          backgroundColor: "#ECECF1",
                          color: "#6E6D92",
                          borderRadius: "8px",
                          p: 0.5,
                          "&:hover": {
                            backgroundColor: "#D8D6E2",
                          },
                        }}
                      >
                        <CloseIcon sx={{ fontSize: "16px" }} />
                      </IconButton>
                    </Box>
                  ) : (
                    <>
                      <Typography
                        sx={{
                          ml: 4,
                          fontSize: "14px",
                          color: "#10100F",
                          fontWeight: 600,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                          textAlign: "right",
                        }}
                      >
                        {field.value}
                      </Typography>

                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 0.5,
                          ml: 1,
                        }}
                      >
                        {field.isPerFill && (
                          <IconButton
                            size="small"
                            onClick={() => {
                              setPerFillInputValue(
                                field.value.replaceAll(/[^0-9.]/g, "") || "150",
                              );
                              setIsEditingPerFill(true);
                            }}
                            sx={{
                              width: "28px",
                              height: "28px",
                              backgroundColor: "#ECECF1",
                              color: "#403C6F",
                              borderRadius: "8px",
                              p: 0.5,
                              "&:hover": {
                                backgroundColor: "#D8D6E2",
                              },
                            }}
                          >
                            <EditIcon sx={{ fontSize: "14px" }} />
                          </IconButton>
                        )}
                        <IconButton
                          size="small"
                          onClick={() =>
                            copyToClipboard(field.value, field.label)
                          }
                          disabled={
                            !field.value ||
                            field.value === "-" ||
                            field.value === "N/A"
                          }
                          sx={{
                            width: "28px",
                            height: "28px",
                            backgroundColor:
                              copiedField === field.label
                                ? "#EBFCEE"
                                : "#ECECF1",
                            color:
                              copiedField === field.label
                                ? "#2D9D42"
                                : "#403C6F",
                            borderRadius: "8px",
                            p: 0.5,
                            "&:hover": {
                              backgroundColor:
                                copiedField === field.label
                                  ? "#EBFCEE"
                                  : "#D8D6E2",
                            },
                          }}
                        >
                          {copiedField === field.label ? (
                            <CheckIcon
                              sx={{ height: "12px" }}
                              fontSize="small"
                            />
                          ) : (
                            <ContentCopyIcon
                              sx={{ height: "12px" }}
                              fontSize="small"
                            />
                          )}
                        </IconButton>
                      </Box>
                    </>
                  )}
                </Box>
              ))}
            </Box>
            {/* Annual Benefit Notes Box */}
            {cardData?.registrationRequestStatus === "AUTOMATED" && (
              <Box
                sx={{
                  mt: 1.5,
                  p: 2,
                  backgroundColor: "#F0F4FA",
                  border: "1px solid #D6E4F7",
                  borderRadius: "12px",
                  textAlign: "left",
                  width: "100%",
                  maxWidth: "460px",
                  margin: "12px auto 0 auto",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    mb: 0.5,
                  }}
                >
                  <InfoOutlinedIcon
                    sx={{ color: "#2B6CB0", fontSize: "18px" }}
                  />
                  <Typography
                    sx={{
                      fontSize: "12px",
                      fontWeight: 700,
                      color: "#2B6CB0",
                    }}
                  >
                    Annual Benefit Notes
                  </Typography>
                </Box>
                <Typography
                  sx={{
                    fontSize: "12px",
                    color: "#4A5568",
                    lineHeight: 1.5,
                  }}
                >
                  {cardData?.annualBenefitNotes ||
                    cardData?.annualBenefitNote ||
                    initialCardData?.annualBenefitNotes ||
                    initialCardData?.annualBenefitNote ||
                    opportunity?.annualBenefitNotes ||
                    opportunity?.annualBenefitNote}
                </Typography>
              </Box>
            )}

            <Box sx={{ display: "flex", gap: 2, mt: 3, width: "100%" }}>
              <Button
                variant="contained"
                onClick={onClose}
                fullWidth
                sx={{
                  borderRadius: "8px",
                  backgroundColor: "#403C6F",
                  color: "#FFFFFF",
                  textTransform: "none",
                  fontWeight: 600,
                  "&:hover": {
                    backgroundColor: "#312e5e",
                  },
                }}
              >
                Close
              </Button>
            </Box>
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
};

CopayCardModal.propTypes = {
  // NOSONAR
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  opportunity: PropTypes.object,
  memberData: PropTypes.object,
  onPersistSuccess: PropTypes.func,
  viewOnly: PropTypes.bool,
  initialCardData: PropTypes.object,
  copayOpportunities: PropTypes.array,
  copayScenario: PropTypes.string,
  copayDetailsMap: PropTypes.object,
};

export default CopayCardModal;
